import { useState } from "react";
import { createConnectQueryKey, useMutation } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { adminListWorkers, adminSetWorkerTier } from "@/gen/nodebase/v1/admin-AdminService_connectquery";
import type { AdminWorker, WorkerTier } from "@/gen/nodebase/v1/admin_pb";
import { WorkerState } from "@/gen/nodebase/v1/worker_pb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ErrorNote } from "@/components/page";

export function WorkerTierDialog({ row, tiers }: { row: AdminWorker; tiers: WorkerTier[] }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [expected, setExpected] = useState(row.worker?.spec);
  const queryClient = useQueryClient();
  const update = useMutation(adminSetWorkerTier, {
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: adminListWorkers, cardinality: undefined }) });
    },
  });
  const worker = row.worker;
  const spec = worker?.spec;
  const change = row.tierChange;
  const applying = change?.state === "applying" || update.isPending;
  const current = tiers.find((tier) => tier.id === row.tier);
  const target = tiers.find((tier) => tier.id === selected);
  const enabled = row.runtime === "native-vm" && (worker?.state === WorkerState.READY || worker?.state === WorkerState.DEGRADED) && tiers.length > 0;
  const summary = spec ? `${spec.vcpu} vCPU · ${spec.memMb / 1024} GiB RAM · ${spec.diskGb} GiB data` : "Capacity unavailable";
  return <div className="max-w-80 space-y-1" data-testid="worker-tier">
    <Dialog open={open} onOpenChange={(next) => {
      if (next) { setSelected(change?.state === "failed" ? change.tier : row.tier); setExpected(spec); update.reset(); }
      setOpen(next);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="min-h-8 px-2 py-1 font-medium text-foreground" disabled={!enabled || applying} data-testid="change-tier">
          {current?.name ?? "Custom"} · Change tier
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Worker tier</DialogTitle>
          <DialogDescription>
            Changing CPU or memory restarts this Worker and interrupts running sessions. Files and the existing {spec?.diskGb} GiB data disk are retained. Memory includes Ubuntu and the Worker agent.
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={(event) => {
          event.preventDefault();
          if (worker && target && !applying) update.mutate({ workerId: worker.id, tier: target.id, expectedSpec: expected });
        }}>
          <ErrorNote error={update.error} />
          <fieldset className="grid grid-cols-3 gap-3" disabled={applying}>
            <legend className="mb-2 text-sm font-medium">Choose a tier</legend>
            {tiers.map((tier) => <label key={tier.id} className={`cursor-pointer rounded-md border p-3 text-sm ${selected === tier.id ? "border-primary bg-muted" : ""}`}>
              <input type="radio" name="worker-tier" value={tier.id} checked={selected === tier.id} onChange={() => setSelected(tier.id)} className="mr-2" />
              <span className="font-medium">{tier.name}</span>
              <span className="mt-2 block">{tier.spec?.vcpu} vCPU</span>
              <span className="block">{(tier.spec?.memMb ?? 0) / 1024} GiB RAM</span>
            </label>)}
          </fieldset>
          <p className="text-sm text-muted-foreground">Current: {current?.name ?? "Custom"}. New Workers start with {tiers[0]?.spec?.diskGb} GiB of data storage; this change keeps your existing disk. Testing-stage tiers have no billing effect.</p>
          <DialogFooter>
            <DialogClose asChild><Button type="button" variant="ghost">Cancel</Button></DialogClose>
            <Button type="submit" disabled={!target || applying || (selected === row.tier && change?.state !== "failed")}>
              {update.isPending ? "Submitting…" : change?.state === "failed" ? "Retry tier change" : "Change tier and restart"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <p className="text-xs text-muted-foreground">{summary}</p>
    {change?.state === "applying" && <p role="status" className="whitespace-normal text-sm text-muted-foreground">Changing to {tiers.find((tier) => tier.id === change.tier)?.name ?? change.tier}… This may take a few minutes. You can leave this page.</p>}
    {change?.state === "failed" && <p role="alert" className="whitespace-normal break-words text-sm text-destructive">Tier change was not verified. {change.error} Last verified capacity is shown above.</p>}
  </div>;
}
